class EventBus {
    constructor(debug) {
        this.events = {};
        this.debug = !!debug;
    }

    log(fn, ...params) {
        if (!this.debug) {
            return;
        }
        const p = [...params ?? []];
        const first = p?.length > 0 ? p[0] : null;
        const rest = p?.length > 1 ? p.slice(1) : [];
        console.log(`%c[${fn}]${first}`, 'color: #888; font-size: 12px;', rest);
    }

    on(event, callback, context = null) {
        try {
            if (!this.events[event]) {
                this.events[event] = [];
            }


            const listener = {
                callback,
                context,
            };
            this.events[event].push(listener);

            this.log('on', `$ event bus on ${event} with context`, { listenersCount: this.events?.[event]?.length, hasCallback: !!listener?.callback });

            return () => this.off(event, callback);
        } catch (e) {
            this.log('on', 'Error on event bus', { event, error: e?.message || String(e), eventNames: Object.keys(this.events || {}) });
        }
    }

    once(event, callback, context = null) {
        const unsubscribe = this.on(
            event,
            (...args) => {
                unsubscribe();
                // Use call with spread instead of apply to avoid HostObject Symbol.toStringTag issues
                callback.call(context, ...args);
            },
            context,
        );

        return unsubscribe;
    }

    off(event, callback) {
        if (!this.events[event]) {
            return false;
        }

        const index = this.events[event].findIndex((listener) => listener.callback === callback);
        if (index !== -1) {
            this.events[event].splice(index, 1);
            if (this.events[event].length === 0) {
                delete this.events[event];
            }
            return true;
        }

        return false;
    }

    emit(event, ...args) {
        if (!this.events || !this.events[event]) {
            console.log('emit', `${event} with args - no events`, { argsLength: args.length });
            return false;
        }

        try {
            const listeners = this.events[event];
            if (Array.isArray(listeners)) {
                listeners.forEach((listener) => {
                    this.log('emit', `${event} with args`, { argsLength: args.length, listenersCount: listeners.length });
                    if (listener && listener.callback) {
                        // Use call with spread instead of apply to avoid HostObject Symbol.toStringTag issues
                        listener.callback.call(listener.context, ...args);
                    }
                });
            }
        } catch (e) {
            this.log('emit', 'Error call bus event', {
                error: e?.message || String(e),
                event,
                argsLength: args?.length || 0,
                eventNames: Object.keys(this.events || {})
            });
        }

        return true;
    }

    eventNames() {
        return Object.keys(this.events);
    }

    listenerCount(event) {
        return this.events[event]?.length || 0;
    }

    removeAllListeners(event) {
        if (event) {
            delete this.events[event];
        } else {
            this.events = {};
        }
    }
}

export const eventBus = new EventBus(false);
